import type { Node } from '@mathflat/answer-expression'
import { z } from 'zod'

import { TrieKeySchema } from '~/@entities/Curriculum'

/***************** 난이도 *****************/
// 문제와 학습지가 같은 축을 쓴다. 1=하 … 5=최상(CMS Level enum 의 value).
export const PROBLEM_LEVEL = [1, 2, 3, 4, 5] as const

/***************** 정답 형식 *****************/
export const PROBLEM_TYPE = ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'SHORT_ANSWER', 'ESSAY'] as const

/***************** 자동채점 판정 *****************/
// 서버 ProblemAutoScoredType 전체. 스펙보다 서버가 앞서 있어(IMPOSSIBLE_EXAMPLE_ANSWER) 서버 소스를 따른다.
export const PROBLEM_AUTO_SCORED_TYPE = [
  'UNIDENTIFIED',
  'IMPOSSIBLE',
  'POSSIBLE_PLUS',
  'POSSIBLE_MINUS',
  'POSSIBLE_UNIT',
  'POSSIBLE_FRACTION',
  'POSSIBLE_DECIMAL',
  'CALCULATION_FRACTION',
  'POSSIBLE_TEXT',
  'ELEMENTARY_KEYPAD',
  'MIDDLE_KEYPAD',
  'HIGH_KEYPAD',
  'OAX_KEYPAD',
  'UNDEFINED',
  'IMPOSSIBLE_MULTI_LIMIT',
  'IMPOSSIBLE_INPUT_LIMIT',
  'IMPOSSIBLE_DESCRIPTIVE',
  'IMPOSSIBLE_DRAWING',
  'IMPOSSIBLE_ETC',
  'IMPOSSIBLE_EXAMPLE_ANSWER',
] as const

/***************** 유사·쌍둥이 추천 묶음 *****************/
export const PROBLEM_GROUP_CASE = ['CONCEPT', 'TOPIC', 'SUB_TOPIC', 'SUB_TOPIC_SPECIAL'] as const

/***************** 자동채점 키패드 *****************/
export const KEYPAD_TYPE = [
  'DECIMAL',
  'FRACTION',
  'MIXED_FRACTION',
  'TEXT',
  'ELEMENTARY',
  'MIDDLE',
  'HIGH',
  'OAX',
] as const

// answerData(정답 AST)에 정답·단위 정보가 들어 있는 키패드. 이 문형은 answer 문자열에서 파생한
// 단위(answerUnits)를 쓰지 않는다.
export const ANSWER_DATA_KEYPAD_TYPE = ['OAX', 'ELEMENTARY', 'MIDDLE', 'HIGH'] as const

/***************** 문제 태그 *****************/
// 선생님이 문제에 붙이는 태그(tagTop)의 종류. 태그 문자열 머리의 대괄호 접두어로 구분한다.
export const TAG_TOP_TYPE = ['twin', 'similar', 'wrong', 'same'] as const

/***************** 유사문제 난이도 *****************/
// 서버 SimilarLevelSearchOption. 기준 문제보다 쉽게·그대로·어렵게.
export const SIMILAR_LEVEL = ['EASIER', 'AS_IS', 'HARDER'] as const

/***************** 문제 출처 *****************/
// 서버 ProblemSourceType. 매쓰플랫 문제은행·선생님이 올린 나의 DB·학원 그룹 교재
export const PROBLEM_SOURCE_TYPE = ['MATHFLAT', 'MY_DB', 'ACADEMY_GROUP_WORKBOOK'] as const

/***************** 모의고사 원본 문제 포함 여부 *****************/
// 서버 PracticeTestOption. 문제를 뽑을 때 모의고사 원본 문제를 포함·제외하거나 그것만 뽑는다.
export const PRACTICE_TEST_OPTION = ['INCLUDE', 'EXCLUDE', 'ONLY'] as const

/***************** 서술형 답안 형식 *****************/
// 정답 형식이 ESSAY 인 문항만 갖는다
export const ESSAY_PROBLEM_TYPE = ['BLANK', 'STEP', 'DESCRIPTIVE', 'HANDWRITING'] as const

export const ProblemLevelSchema = z.literal(PROBLEM_LEVEL)
export type ProblemLevel = z.infer<typeof ProblemLevelSchema>

export const ProblemTypeSchema = z.enum(PROBLEM_TYPE)
export type ProblemType = z.infer<typeof ProblemTypeSchema>

export const ProblemAutoScoredTypeSchema = z.enum(PROBLEM_AUTO_SCORED_TYPE)
export type ProblemAutoScoredType = z.infer<typeof ProblemAutoScoredTypeSchema>

// 서버 enum 의 autoScored=true 인 판정. 교재 문제는 autoScored 를 싣지 않아 판정에서 가능 여부를 읽는다.
const AUTO_SCORABLE_TYPES: ReadonlySet<ProblemAutoScoredType> = new Set([
  'POSSIBLE_PLUS',
  'POSSIBLE_MINUS',
  'POSSIBLE_UNIT',
  'POSSIBLE_FRACTION',
  'POSSIBLE_DECIMAL',
  'CALCULATION_FRACTION',
  'POSSIBLE_TEXT',
  'ELEMENTARY_KEYPAD',
  'MIDDLE_KEYPAD',
  'HIGH_KEYPAD',
  'OAX_KEYPAD',
] satisfies ProblemAutoScoredType[])

/** 이 판정의 문항을 자동채점할 수 있는가 */
export const isAutoScorableType = (autoScoredType: ProblemAutoScoredType) =>
  AUTO_SCORABLE_TYPES.has(autoScoredType)

export const ProblemGroupCaseSchema = z.enum(PROBLEM_GROUP_CASE)
export type ProblemGroupCase = z.infer<typeof ProblemGroupCaseSchema>

export const KeypadTypeSchema = z.enum(KEYPAD_TYPE)
export type KeypadType = z.infer<typeof KeypadTypeSchema>

export const TagTopTypeSchema = z.enum(TAG_TOP_TYPE)
export type TagTopType = z.infer<typeof TagTopTypeSchema>

export const SimilarLevelSchema = z.enum(SIMILAR_LEVEL)
export type SimilarLevel = z.infer<typeof SimilarLevelSchema>

export const ProblemSourceTypeSchema = z.enum(PROBLEM_SOURCE_TYPE)
export type ProblemSourceType = z.infer<typeof ProblemSourceTypeSchema>

export const PracticeTestOptionSchema = z.enum(PRACTICE_TEST_OPTION)
export type PracticeTestOption = z.infer<typeof PracticeTestOptionSchema>

export const EssayProblemTypeSchema = z.enum(ESSAY_PROBLEM_TYPE)
export type EssayProblemType = z.infer<typeof EssayProblemTypeSchema>

/** 문제 이미지 위 입력칸 영역(슬롯 레이아웃)은 단계형·빈칸형에만 있다 */
export const hasEssaySlotLayout = (essayType: EssayProblemType) =>
  essayType === 'STEP' || essayType === 'BLANK'

/** 답안을 슬롯이 아니라 문제 이미지 위 자유 필기로 제출하는 유형 */
export const isEssayHandwritingType = (essayType: EssayProblemType) => essayType === 'HANDWRITING'

/** 답안 칸이 1개뿐인 유형(서술형·필기형)은 칸 번호를 붙여도 가리킬 대상이 없다 */
export const hasEssayAnswerSlotNumber = (essayType: EssayProblemType) =>
  essayType === 'STEP' || essayType === 'BLANK'

export const isAnswerDataKeypadTypes = (keypadTypes: readonly string[] | null | undefined) =>
  !!keypadTypes?.some((type) => (ANSWER_DATA_KEYPAD_TYPE as readonly string[]).includes(type))

/***************** 문제 본체 *****************/

// 정답 앞뒤에 붙는 단위. index 0 은 앞, 그 밖은 뒤다.
export const AnswerUnitSchema = z.object({
  unit: z.string(),
  index: z.number(),
})
export type AnswerUnit = z.infer<typeof AnswerUnitSchema>

export const ProblemSummarySchema = z.object({
  problemId: z.number(),
  totalUsed: z.number(),
  correctTimes: z.number(),
  wrongTimes: z.number(),
  answerRate: z.number(),
})
export type ProblemSummary = z.infer<typeof ProblemSummarySchema>

// 문제 풀이 동영상. title·videoLengthSeconds 는 앱이 읽지 않아 받지 않는다.
export const ProblemVideoSchema = z.object({
  id: z.number(),
  videoUrl: z.string(),
  thumbnailUrl: z.string(),
  subtitleUrl: z.string().nullable(),
})
export type ProblemVideo = z.infer<typeof ProblemVideoSchema>

// 정답 AST. 구조 검증은 @mathflat/answer-expression 의 파서가 맡고, 경계에서는 객체인지만 본다.
const _AnswerDataSchema = z.custom<Node>((value) => typeof value === 'object' && value !== null)

/**
 * 문제은행의 문제. 교재 문제(WorkbookProblem)·학습지 문제(WorksheetProblem)와 키가 비슷하지만 다른 것이다.
 * 제외한 서버 필드:
 * - levelOfConceptChip: 유형칩 난이도(A·B·C). 앱은 유형칩의 난이도를 유형칩 응답에서 읽는다
 * - sourceType: 문제 출처. 앱은 학습지·수업 응답이 싣는 자기 출처 필드를 읽는다
 * - subTopicPriority·practiceProblemYear: V2 응답에만 있고 앱이 읽지 않는다
 */
export const ProblemSchema = z.object({
  id: z.number(),
  conceptId: z.number(),
  groupCode: z.number().nullable(),
  topicId: z.number().nullable(),
  subTopicId: z.number().nullable(),
  groupCase: ProblemGroupCaseSchema.nullable(),
  type: ProblemTypeSchema,
  optionCount: z.number(),
  level: ProblemLevelSchema,
  problemImageUrl: z.string(),
  // 정답을 숨기는 응답(학생 학습지·시험)은 정답·해설 URL 과 answer 를 빈 문자열로 보낸다.
  answerImageUrl: z.string(),
  solutionImageUrl: z.string(),
  answer: z.string(),
  answerUnits: z.array(AnswerUnitSchema),
  answerCount: z.number(),
  autoScoredType: ProblemAutoScoredTypeSchema,
  autoScored: z.boolean(),
  keypadTypes: z.array(KeypadTypeSchema),
  // 단일 입력 문제는 null 이다. V2 응답은 이 필드를 싣지 않는다.
  answerData: _AnswerDataSchema.nullable(),
  hidden: z.boolean(),
  trendy: z.boolean(),
  sample: z.boolean(),
  actualSize: z.boolean(),
  problemSummary: ProblemSummarySchema,
  video: ProblemVideoSchema.nullable(),
  // 풀이 동영상이 이 문제 자체의 것인지(유사 문제의 동영상을 빌려 온 것이 아닌지)
  videoMatched: z.boolean(),
})
export type Problem = z.infer<typeof ProblemSchema>

/** 학생에게 배정된 학습지가 싣는 문제. 유형 이름과 선생님 태그가 더 붙는다. 함께 오는 index·favorite 는 앱이 읽지 않아 받지 않는다 */
export const TaggedProblemSchema = ProblemSchema.extend({
  conceptName: z.string().nullable(),
  tagTop: z.string().nullable(),
})
export type TaggedProblem = z.infer<typeof TaggedProblemSchema>

/***************** 서술형 블록 *****************/
/** 서술형 문항의 입력 슬롯 — 좌표는 문제 이미지 기준 0~1 정규화(좌상단 원점) */
export const EssayProblemSlotSchema = z.object({
  // 1-based 슬롯 번호
  tag: z.number(),
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  // 단계 제목 등
  label: z.string().nullable(),
  // 빈칸 정답. 빈칸형만 있고 학생 호출에는 서버가 비운다
  answer: z.string().nullable(),
})
export type EssayProblemSlot = z.infer<typeof EssayProblemSlotSchema>

export const EssaySlotHandwritingSchema = z.object({
  tag: EssayProblemSlotSchema.shape.tag,
  handwritingUrl: z.string(),
})
export type EssaySlotHandwriting = z.infer<typeof EssaySlotHandwritingSchema>

/** 문항 조회 응답의 서술형 확장 블록 */
export const EssayProblemSchema = z.object({
  essayType: EssayProblemTypeSchema,
  // 100점을 문항 수로 나눈 몫에 나머지를 난이도 높은 문항부터 1씩 얹는다
  maxScore: z.number(),
  // 완전서술형도 문항 전체 영역 슬롯 1개가 온다. 필기형만 빈 배열이다
  slots: z.array(EssayProblemSlotSchema),
  // 풀이 중 자동저장분 — 재진입 복원용. 제출 뒤에는 채점 조회가 정본이다
  slotHandwritings: z.array(EssaySlotHandwritingSchema),
  // 조건분석([구하려는 것]/[주어진 조건]) 채점 미포함 안내 배너 노출 여부
  infoBannerFlag: z.boolean(),
})
export type EssayProblem = z.infer<typeof EssayProblemSchema>

/***************** 문제 묶음의 교육과정 *****************/
// 고른 문제들의 유형 중 교육과정 순서가 가장 앞선 것의 학기 단위 트라이 키. 서버 선언은 nullable 이지만 지금 구현은
// 늘 채우고, 유형을 찾지 못하면 오류로 응답한다. 2026-10-14 백엔드 develop(6a0f8e7bb) 의
// problem/manager/ProblemManager.kt·controller/response/ProblemMinimumTrieKeyResponse.kt 로 확인.
export const ProblemMinimumTrieKeySchema = z.object({
  trieKey: TrieKeySchema.nullable(),
})
export type ProblemMinimumTrieKey = z.infer<typeof ProblemMinimumTrieKeySchema>
